// PaymentSuccess: Success page for completed Stripe payments
// Handles post-checkout success flow and guides users to next steps

import React, { useEffect } from 'react'
import { useCredits } from '@/hooks/useCredits'
import { useSubscription } from '@/hooks/useSubscription'

interface PaymentSuccessProps {
  onContinue?: () => void
  className?: string
}

export function PaymentSuccess({ onContinue, className = '' }: PaymentSuccessProps) {
  const { refreshBalance } = useCredits()
  const { subscription, refreshSubscription } = useSubscription()

  // Refresh subscription and credit data when component mounts
  useEffect(() => {
    const refreshData = async () => {
      try {
        await Promise.all([
          refreshSubscription(),
          refreshBalance()
        ])
      } catch (error) {
        console.error('Failed to refresh billing data:', error)
      }
    }

    refreshData()
  }, [refreshSubscription, refreshBalance])

  // Get plan name for dynamic welcome message
  const planName = subscription?.plan_name || 'Premium'

  const handleContinue = () => {
    if (onContinue) {
      onContinue()
    } else {
      // Default: redirect to upload/workspace
      window.location.href = '/#upload'
    }
  }

  return (
    <div className={`max-w-2xl mx-auto text-center py-12 ${className}`}>
      <div className="bg-white rounded-2xl shadow-lg border p-8 space-y-6">
        {/* Success Icon */}
        <div className="flex justify-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <svg 
              className="w-8 h-8 text-green-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M5 13l4 4L19 7" 
              />
            </svg>
          </div>
        </div>

        {/* Success Message */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-gray-900">
            Payment Successful! 🎉
          </h1>
          <p className="text-lg text-gray-600">
            Welcome to StockTagger.ai {planName}
          </p>
        </div>

        {/* Benefits Reminder */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-left">
          <h3 className="font-semibold text-blue-900 mb-3">What's Next?</h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-center">
              <svg className="w-4 h-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Your credits have been added to your account
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Start uploading images and generating AI metadata
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Manage your subscription anytime in Account settings
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <button
            onClick={handleContinue}
            className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Start Creating Metadata
          </button>
          <button
            onClick={() => window.location.href = '/#account'}
            className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            View Account
          </button>
        </div>

        {/* Support Note */}
        <div className="text-xs text-gray-500 pt-4 border-t">
          <p>
            Having trouble? Contact us at{' '}
            <a 
              href="mailto:support@stocktagger.ai" 
              className="text-blue-600 hover:text-blue-800"
            >
              support@stocktagger.ai
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default PaymentSuccess